import React, { useState, useEffect } from 'react';
import {
  Play,
  BarChart3,
  Maximize2,
  Minimize2,
  Beer,
  GlassWater
} from 'lucide-react';
import {
  HudPanel,
  ConditionCard,
  GameButton,
  CampaignBadge,
  ArrowKeysHint,
  RoadcastLogo
} from './roadcastUI';

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const requestFullscreen = () => {
  const el = document.documentElement;
  const req =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.webkitRequestFullScreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (req) {
    try {
      const p = req.call(el);
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) { /* iPhone Safari blocks this */ }
  }
};

const exitFullscreen = () => {
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.webkitCancelFullScreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;
  if (exit) {
    try {
      const p = exit.call(document);
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) { /* noop */ }
  }
};

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.webkitCurrentFullScreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement ||
  null;

// Custom steering wheel icon (thin lines, Roadcast style)
const SteeringWheel = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2.2" />
    <path d="M12 4v6" />
    <path d="M4.5 15.5l5.5 -3" />
    <path d="M19.5 15.5l-5.5 -3" />
  </svg>
);

// Small lane-drift illustration used in the right HUD widget
const LaneDriftGlyph = () => (
  <svg width="100%" height="140" viewBox="0 0 120 140" fill="none" stroke="var(--rc-teal)" strokeWidth="1.2">
    <rect x="6" y="4" width="108" height="132" rx="8" stroke="var(--rc-border-subtle)" />
    {/* Lane markers */}
    {[18, 44, 70, 96].map((y) => (
      <g key={y}>
        <line x1="40" y1={y} x2="40" y2={y + 10} strokeDasharray="4 6" opacity="0.5" />
        <line x1="80" y1={y} x2="80" y2={y + 10} strokeDasharray="4 6" opacity="0.5" />
      </g>
    ))}
    {/* Curved drift path */}
    <path d="M60 130 C 50 100, 78 80, 62 60 C 46 40, 74 24, 60 8" stroke="var(--rc-teal-bright)" strokeWidth="1.5" opacity="0.85" />
    {/* Car icon at bottom */}
    <g transform="translate(52, 88)">
      <rect x="0" y="0" width="16" height="26" rx="4" stroke="var(--rc-teal-bright)" strokeWidth="1.4" fill="rgba(34,211,195,0.14)" />
      <line x1="4" y1="6" x2="12" y2="6" strokeWidth="1" opacity="0.7" />
      <line x1="4" y1="20" x2="12" y2="20" strokeWidth="1" opacity="0.7" />
    </g>
  </svg>
);

// Small BAC gauge (semicircle)
const BacGauge = ({ value = 0.08 }) => {
  // Map 0–0.30 BAC → 0–π
  const bounded = Math.max(0, Math.min(0.3, value));
  const angle = (bounded / 0.3) * Math.PI;
  const cx = 60;
  const cy = 60;
  const r = 44;
  const x = cx - r * Math.cos(angle);
  const y = cy - r * Math.sin(angle);
  return (
    <svg width="130" height="80" viewBox="0 0 120 80" fill="none">
      {/* Ticks */}
      <path d="M16 60 A44 44 0 0 1 104 60" stroke="rgba(34,211,195,0.28)" strokeWidth="1" />
      <path d="M16 60 A44 44 0 0 1 104 60" stroke="var(--rc-teal-bright)"
            strokeWidth="1.6" strokeDasharray={`${bounded / 0.3 * 138} 200`} strokeLinecap="round" />
      {/* Labels */}
      {[
        { v: '0.06', ang: 0.15 },
        { v: '0.18', ang: 0.5 },
        { v: '0.25', ang: 0.85 }
      ].map(({ v, ang }) => {
        const lx = cx - (r + 10) * Math.cos(ang * Math.PI);
        const ly = cy - (r + 10) * Math.sin(ang * Math.PI);
        return (
          <text key={v} x={lx} y={ly + 3} textAnchor="middle"
                fontSize="8" fill="rgba(220,232,231,0.5)" fontFamily="JetBrains Mono, monospace">
            {v}
          </text>
        );
      })}
      {/* Needle */}
      <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--rc-teal-bright)" strokeWidth="2" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="3" fill="var(--rc-teal-bright)" />
    </svg>
  );
};

// Roadcast-style small no-drink shield icon
const NoDrinkBadge = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--rc-danger)" strokeWidth="1.6">
    <circle cx="12" cy="12" r="9" />
    <path d="M5.5 5.5 l13 13" />
    <path d="M8 8 h3 v6 c0 1 -1 2 -2 2" strokeLinejoin="round" />
  </svg>
);

const StartScreen = ({ onStart, onViewStats }) => {
  const [selectedDrinks, setSelectedDrinks] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHomeScreenHint, setShowHomeScreenHint] = useState(false);
  const showFullscreen = isTouchDevice();

  const supportsFullscreenApi =
    typeof document !== 'undefined' &&
    !!(
      document.documentElement.requestFullscreen ||
      document.documentElement.webkitRequestFullscreen ||
      document.documentElement.webkitRequestFullScreen
    );
  const isIphone =
    typeof navigator !== 'undefined' &&
    /iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream;

  useEffect(() => {
    const update = () => setIsFullscreen(!!getFullscreenElement());
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];
    events.forEach((e) => document.addEventListener(e, update));
    update();
    return () => events.forEach((e) => document.removeEventListener(e, update));
  }, []);

  const toggleFullscreen = () => {
    if (isIphone || !supportsFullscreenApi) {
      setShowHomeScreenHint(true);
      return;
    }
    if (getFullscreenElement()) exitFullscreen();
    else requestFullscreen();
  };

  const handleStart = () => {
    if (selectedDrinks === null) return;
    if (showFullscreen && supportsFullscreenApi && !getFullscreenElement()) {
      requestFullscreen();
    }
    onStart(selectedDrinks);
  };

  // Card definitions — labels match reference, internal values preserved
  // so existing gameplay/BAC logic doesn't change.
  const conditions = [
    {
      count: 0,
      title: 'SOBER',
      description: '0 drinks • clear control',
      tone: 'teal',
      icon: <SteeringWheel size={26} />
    },
    {
      count: 3,
      title: 'BUZZED',
      description: '2 drinks • delayed response',
      tone: 'amber',
      icon: <Beer size={26} />
    },
    {
      count: 6,
      title: 'VERY DRUNK',
      description: '3+ drinks • major impairment',
      tone: 'danger',
      icon: <GlassWater size={26} />
    }
  ];

  return (
    <div
      className="absolute inset-0"
      data-testid="start-screen"
      style={{
        overflow: 'hidden',
        color: 'var(--rc-text-primary)',
        fontFamily: 'var(--rc-font-display)'
      }}
    >
      {/* Background — wet night highway + teal atmospheric glow */}
      <div
        className="rc-road-bg"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1519638399535-1b036603ac77?auto=format&fit=crop&w=1920&q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 40% at 50% 55%, rgba(34,211,195,0.12) 0%, transparent 65%),' +
            'linear-gradient(180deg, rgba(4,10,12,0.6) 0%, rgba(4,10,12,0.82) 40%, rgba(3,7,8,0.95) 100%)'
        }}
      />
      <div className="rc-vignette" />
      {/* Corner HUD ticks */}
      <div className="rc-corner-frame"><span /><span /></div>

      {/* Left HUD widgets (desktop/tablet) */}
      <div
        className="rc-desktop-hud"
        style={{
          position: 'absolute',
          top: 26,
          left: 26,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          zIndex: 5
        }}
      >
        {/* RoadCast brand lockup */}
        <div
          data-testid="brand-logo-wrapper"
          className="rc-enter"
          style={{
            background: 'rgba(9, 20, 24, 0.72)',
            border: '1px solid var(--rc-border-subtle)',
            borderRadius: 'var(--rc-radius-md)',
            padding: '10px 16px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--rc-panel-glow)'
          }}
        >
          <RoadcastLogo height={44} testId="start-brand-logo" />
        </div>
        <HudPanel label="BAC %" testId="hud-bac">
          <div
            style={{
              fontFamily: 'var(--rc-font-display)',
              fontWeight: 900,
              fontSize: 26,
              color: 'var(--rc-teal-bright)',
              lineHeight: 1
            }}
          >
            0.08%
          </div>
          <div style={{ marginTop: 6 }}>
            <BacGauge value={0.08} />
          </div>
        </HudPanel>
      </div>

      {/* Right HUD widget (desktop/tablet) */}
      <div
        className="rc-desktop-hud"
        style={{
          position: 'absolute',
          top: 26,
          right: 26,
          zIndex: 5
        }}
      >
        <HudPanel label="Lane Drift" testId="hud-lane-drift" style={{ width: 168 }}>
          <LaneDriftGlyph />
        </HudPanel>
      </div>

      {/* Center content */}
      <div className="rc-card-modal-container">
        {/* Mobile-only Top Brand Header */}
        <div className="block sm:hidden md:hidden lg:hidden rc-mobile-brand" style={{ marginBottom: 2 }}>
          <RoadcastLogo height={28} testId="start-brand-logo-mobile" />
        </div>

        {/* Title */}
        <div className="rc-enter" style={{ textAlign: 'center', marginTop: 2 }}>
          <h1
            data-testid="game-title"
            style={{
              fontFamily: 'var(--rc-font-display)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              fontSize: 'clamp(22px, 4.8vw, 64px)',
              lineHeight: 1.05,
              margin: 0
            }}
          >
            <span style={{ color: 'var(--rc-text-primary)' }}>ALCOCHEK </span>
            <span style={{
              color: 'var(--rc-teal-bright)',
              textShadow: '0 0 18px rgba(34, 211, 195, 0.4)'
            }}>
              DRIVE
            </span>
          </h1>
          <div
            style={{
              marginTop: 2,
              color: 'var(--rc-text-secondary)',
              fontSize: 'clamp(10px, 1.1vw, 15px)',
              letterSpacing: '0.02em'
            }}
          >
            Educational impairment simulation
          </div>
        </div>

        {/* Condition cards */}
        <div
          className="rc-enter rc-enter-delay-1"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            justifyContent: 'center',
            gap: 'clamp(6px, 1.4vw, 14px)',
            width: '100%',
            maxWidth: 820
          }}
        >
          {conditions.map((c) => (
            <ConditionCard
              key={c.count}
              active={selectedDrinks === c.count}
              onClick={() => setSelectedDrinks(c.count)}
              tone={c.tone}
              title={c.title}
              description={c.description}
              icon={c.icon}
              testId={`drink-select-${c.count}`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div
          className="rc-enter rc-enter-delay-2"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 8,
            marginTop: 2
          }}
        >
          <GameButton
            variant="primary"
            onClick={handleStart}
            disabled={selectedDrinks === null}
            icon={<Play size={14} fill="currentColor" />}
            testId="start-simulation-button"
          >
            Start Simulation
          </GameButton>
          <GameButton
            variant="secondary"
            onClick={onViewStats}
            icon={<BarChart3 size={16} />}
            testId="view-stats-button"
          >
            View Stats
          </GameButton>
          {showFullscreen && (
            <GameButton
              variant="secondary"
              onClick={toggleFullscreen}
              icon={isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              testId="fullscreen-toggle-button"
            >
              {isFullscreen ? 'Exit' : 'Full'}
            </GameButton>
          )}
        </div>

        {/* Arrow keys hint (desktop only) */}
        <div className="rc-enter rc-enter-delay-3 rc-desktop-hud">
          <ArrowKeysHint />
        </div>

        {/* Campaign badge */}
        <div className="rc-enter rc-enter-delay-4" style={{ marginTop: '0.5vh', marginBottom: 12 }}>
          <CampaignBadge />
        </div>
      </div>

      {/* Top-right corner: drink-drive-pay chip */}
      <div
        style={{
          position: 'absolute',
          top: 26,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'none' // reserved slot; not shown on start screen
        }}
      />

      {showHomeScreenHint && (
        <HomeScreenHint isIphone={isIphone} onClose={() => setShowHomeScreenHint(false)} />
      )}
    </div>
  );
};

const HomeScreenHint = ({ isIphone, onClose }) => (
  <div
    data-testid="home-screen-hint"
    onClick={onClose}
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 90,
      background: 'rgba(2,7,9,0.88)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'var(--rc-font-display)',
      cursor: 'pointer'
    }}
  >
    <div
      className="rc-panel"
      onClick={(e) => e.stopPropagation()}
      style={{
        maxWidth: 440,
        padding: '28px 26px',
        textAlign: 'left',
        cursor: 'default'
      }}
    >
      <div className="rc-tag" style={{ marginBottom: 10 }}>Fullscreen Not Supported</div>
      <h3 style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 14, color: 'var(--rc-text-primary)' }}>
        {isIphone ? 'iOS blocks fullscreen on iPhone' : 'Your browser blocks fullscreen'}
      </h3>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--rc-text-secondary)', marginBottom: 16 }}>
        For a truly immersive experience, install this game to your Home Screen —
        it will launch without any browser UI.
      </p>
      <ol style={{ paddingLeft: 20, fontSize: 14, lineHeight: 1.7, color: 'var(--rc-text-primary)' }}>
        {isIphone ? (
          <>
            <li>Open this page in <b>Safari</b> (not Chrome).</li>
            <li>Tap the <b>Share</b> icon at the bottom.</li>
            <li>Tap <b>Add to Home Screen</b> → <b>Add</b>.</li>
            <li>Launch <b>Alcochek Drive</b> from your home screen.</li>
          </>
        ) : (
          <>
            <li>Open the browser menu (<b>⋮</b> or <b>⋯</b>).</li>
            <li>Tap <b>Install app</b> or <b>Add to Home Screen</b>.</li>
            <li>Launch <b>Alcochek Drive</b> from your home screen.</li>
          </>
        )}
      </ol>
      <button
        data-testid="home-screen-hint-close"
        onClick={onClose}
        className="rc-primary-btn"
        style={{ marginTop: 20, width: '100%', justifyContent: 'center' }}
      >
        Got it
      </button>
    </div>
  </div>
);

export default StartScreen;
